import { IconTrash } from "@tabler/icons-react";
import { Button, Input, Space, Tooltip, Typography } from "antd";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useUpdateProject } from "@/common/artemis-operations/projects";
import type { Project, ProjectStatus } from "@/common/model";
import { toLabel } from "@/common/utils";
import { CustomIcon, IdField } from "../base";
import {
	BaseDetails,
	EditableText,
	InlineSelect,
	toOptions,
} from "../base-details";
import { CardViewer } from "../card-viewer";
import { DocumentEditor } from "../document-editor";
import { CustomDrawer } from "../layout/workspace/custom-drawer";
import { ResourceLinksGrid } from "../resource-links";
import { OwnerField } from "../users/owner-field";
import { ProjectStatusBadge, ProjectStatusLabelMap } from "./project-badges";

interface ProjectDetailsProps {
	project: NonNullable<Project>;
	onSaveName?: (value: string) => void;
	onSaveDescription?: (value: string) => void;
	onSaveStatus?: (value: ProjectStatus) => void;
	/** Hands ownership to another workspace member. When omitted, the owner is read-only. */
	onSaveOwner?: (userId: string) => void;
}

export function ProjectDetails({
	project,
	onSaveName,
	onSaveDescription,
	onSaveStatus,
	onSaveOwner,
}: ProjectDetailsProps) {
	return (
		<BaseDetails
			item={project}
			fields={[
				{
					key: "id",
					label: "Project ID",
					render: (id) => <IdField id={id} showCopyButton />,
				},
				{
					key: "name",
					label: "Name",
					render: (name) => (
						<EditableText onChange={onSaveName}>{name as string}</EditableText>
					),
				},
				{
					key: "status",
					label: "Status",
					render: (status) => (
						<InlineSelect
							value={status}
							options={toOptions(ProjectStatusLabelMap)}
							onChange={onSaveStatus}
						>
							<ProjectStatusBadge status={status} />
						</InlineSelect>
					),
				},
				{
					key: "summary",
					label: "Summary",
					render: (summary) => toLabel(summary as string),
				},
				{
					key: "ownerId",
					label: "Owner",
					render: (id) => (
						<OwnerField
							ownerId={id}
							workspaceId={project.workspaceId}
							onSave={onSaveOwner}
						/>
					),
				},
				{
					key: "createdAt",
					label: "Created",
					render: (createdAt) =>
						createdAt
							? format(new Date(createdAt as string), "MM/dd/yyyy h:mm a")
							: toLabel(null),
				},
				{
					key: "description",
					label: "Description",
					render: (description) =>
						onSaveDescription || description ? (
							<EditableText
								paragraph
								style={{ marginBottom: 0, width: "100%" }}
								onChange={onSaveDescription}
							>
								{description as string}
							</EditableText>
						) : (
							toLabel(null)
						),
				},
			]}
		/>
	);
}

export function ProjectDetailsDrawer({
	project,
	open,
	onClose,
	onSaveName,
	onSaveDescription,
	onSaveStatus,
}: {
	project?: NonNullable<Project>;
	open: boolean;
	onClose: () => void;
	onSaveName?: (value: string) => void;
	onSaveDescription?: (value: string) => void;
	onSaveStatus?: (value: ProjectStatus) => void;
}) {
	if (!project) return null;
	return (
		<CustomDrawer title={project.name} open={open} onClose={onClose}>
			<ProjectDetails
				project={project}
				onSaveName={onSaveName}
				onSaveDescription={onSaveDescription}
				onSaveStatus={onSaveStatus}
			/>
		</CustomDrawer>
	);
}

/** Drawer to add or edit a single note. Add mode = omit `onDelete`. */
export function NoteDrawer({
	open,
	initialValue,
	onSave,
	onDelete,
	onClose,
}: {
	open: boolean;
	initialValue: string;
	onSave: (value: string) => void;
	onDelete?: () => void;
	onClose: () => void;
}) {
	const [draft, setDraft] = useState(initialValue);

	// Sync the draft to the note being opened (or reset to empty when adding).
	useEffect(() => {
		if (open) setDraft(initialValue);
	}, [open, initialValue]);

	return (
		<CustomDrawer
			title="Note"
			open={open}
			onClose={onClose}
			extra={
				<Space>
					{onDelete && (
						<Tooltip title="Delete note">
							<Button
								danger
								type="text"
								icon={<CustomIcon icon={IconTrash} />}
								onClick={onDelete}
							/>
						</Tooltip>
					)}
					<Button type="primary" onClick={() => onSave(draft)}>
						Save
					</Button>
				</Space>
			}
		>
			<Input.TextArea
				autoSize={{ minRows: 8 }}
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
			/>
		</CustomDrawer>
	);
}

/** Grid of note cards; clicking a card opens a drawer to edit or delete it. */
export function ProjectNotesGrid({
	project,
}: {
	project: NonNullable<Project>;
}) {
	const updateProject = useUpdateProject(project.id);
	const notes = project.notes ?? [];
	const [editing, setEditing] = useState<number | null>(null);
	const close = () => setEditing(null);

	const saveNote = (value: string) => {
		if (editing === null) return;
		updateProject({ notes: notes.map((n, i) => (i === editing ? value : n)) });
		close();
	};
	const deleteNote = () => {
		if (editing === null) return;
		updateProject({ notes: notes.filter((_, i) => i !== editing) });
		close();
	};

	return (
		<>
			<CardViewer<{ note: string; index: number }>
				items={notes.map((note, index) => ({ note, index }))}
				getKey={(n) => `${n.index}`}
				onOpen={(n) => setEditing(n.index)}
				renderCard={(n) => (
					<Typography.Paragraph
						style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}
						ellipsis={{ rows: 5 }}
					>
						{n.note}
					</Typography.Paragraph>
				)}
			/>

			<NoteDrawer
				open={editing !== null}
				initialValue={editing !== null ? notes[editing] : ""}
				onSave={saveNote}
				onDelete={deleteNote}
				onClose={close}
			/>
		</>
	);
}

/** Displays the instructions for a project. */
export function ProjectInstructions({
	project,
}: {
	project: NonNullable<Project>;
}) {
	return (
		<DocumentEditor
			content={project.instructions}
			viewMode={"preview"}
			readOnly={true}
		/>
	);
}

/** Displays the links for a project; clicking a card opens a drawer to edit or delete it. */
export function ProjectLinks({
	project,
	onAdd,
}: {
	project?: Project;
	/** Opens the add-link dialog from the empty state. */
	onAdd?: () => void;
}) {
	const updateProject = useUpdateProject(project?.id ?? "");
	if (!project) return null;

	return (
		<ResourceLinksGrid
			links={project.links ?? []}
			onSave={(links) => updateProject({ links })}
			onAdd={onAdd}
		/>
	);
}
